import React from "react";
import { Heading, Icon, Notification, StepByStep, Text } from "@telia/teddy";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../models";
import PortingDetailsCard from "../../components/atoms/PortingDetailsCard/PortingDetailsCard";
import { getCustomerTypeGet, getNumberOfItemsInCart, getPortingDates } from "../../state/selectors/cartSelectors";
import {
    numberOfPortInEsimSubscriptions as numberOfPortInEsimSubscriptionsSelector,
    numberOfPortInSubscriptions as numberOfPortInSubscriptionsSelector,
    isDigitalDelivery as isDigitalDeliverySelector,
    hasAtLeaseOneSubscriptionWithConvergenceOffer as hasAtLeaseOneSubscriptionWithConvergenceOfferSelector,
} from "../../state/selectors/combinedSelectors";
import { getDeliveryInfoEstimatedDate, getDeliveryInfoSelected } from "../../state/selectors/deliveryInfoSelectors";
import { hasTransferSubscriptionFromOtherOwner as hasTransferSubscriptionFromOtherOwnerSelector } from "../../state/selectors/subscriptionsSelectors";
import "./ReceiptNextSteps.less";
import TEXT from "../../text-consts";
import { GET_CUSTOMER_TYPE } from "../../../../../constants";

const ReceiptNextSteps: React.FC = () => {
    const customerTypeGet = useSelector((state: RootState) => getCustomerTypeGet(state));
    const isTeliaSamlet = customerTypeGet === GET_CUSTOMER_TYPE.EXISTING;
    const hasAtLeaseOneSubscriptionWithConvergenceOffer = useSelector((state: RootState) =>
        hasAtLeaseOneSubscriptionWithConvergenceOfferSelector(state),
    );
    const hasDoubleDataDisclaimer = isTeliaSamlet && hasAtLeaseOneSubscriptionWithConvergenceOffer;
    const isDigitalDelivery = useSelector((state: RootState) => isDigitalDeliverySelector(state));
    const deliveryEstimatedDate = useSelector((state: RootState) => getDeliveryInfoEstimatedDate(state));
    const portingDates = useSelector((state: RootState) => getPortingDates(state));
    const numberOfPortInEsimSubscriptions = useSelector((state: RootState) =>
        numberOfPortInEsimSubscriptionsSelector(state),
    );
    const hasTransferSubscriptionFromOtherOwner = useSelector((state: RootState) =>
        hasTransferSubscriptionFromOtherOwnerSelector(state),
    );
    const numberOfPortInSubscriptions = useSelector((state: RootState) => numberOfPortInSubscriptionsSelector(state));
    const hasPortingSubscriptions = numberOfPortInEsimSubscriptions > 0 || numberOfPortInSubscriptions > 0;
    const hasSteps = !isDigitalDelivery || hasPortingSubscriptions || hasTransferSubscriptionFromOtherOwner;

    const deliveryInfo = useSelector((state: RootState) => getDeliveryInfoSelected(state));
    const isClickAndCollect = deliveryInfo?.type === "PICK_UP_IN_SHOP";
    const numberOfItems = useSelector((state: RootState) => getNumberOfItemsInCart(state));

    if (!hasSteps && !hasDoubleDataDisclaimer) {
        return null;
    }

    return (
        <div className="receipt-next-steps">
            {hasSteps && (
                <>
                    <Heading as="h2" variant="title-300" mb="200">
                        Dette skjer videre
                    </Heading>
                    <StepByStep size="md" standalone>
                        {!isDigitalDelivery && !isClickAndCollect && (
                            <StepByStep.Step>
                                <StepByStep.Description
                                    heading="Levering"
                                    icon={<Icon name="delivery" aria-hidden="true" focusable="false" />}
                                >
                                    <Text className="receipt-next-steps__step-description">
                                        Estimert leveringsdato innen{" "}
                                        <Text ml="50" as="span" variant="paragraph-100-bold">
                                            {deliveryEstimatedDate}
                                        </Text>
                                    </Text>
                                </StepByStep.Description>
                            </StepByStep.Step>
                        )}
                        {isClickAndCollect && (
                            <>
                                <StepByStep.Step>
                                    <StepByStep.Description
                                        heading="Du vil få en SMS"
                                        icon={<Icon name="sms" aria-hidden="true" focusable="false" />}
                                    >
                                        <Text>
                                            Vi sender deg en SMS når varen{numberOfItems > 1 ? "e" : ""} er klar
                                            {numberOfItems > 1 ? "e" : ""} for henting (normalt innen 1 time i butikkens
                                            åpningstid)
                                        </Text>
                                    </StepByStep.Description>
                                </StepByStep.Step>
                                <StepByStep.Step>
                                    <StepByStep.Description
                                        heading="Hent i butikk"
                                        icon={<Icon name="store" aria-hidden="true" focusable="false" />}
                                    >
                                        <Text>
                                            Du kan hente bestillingen din hos: <strong>{deliveryInfo?.shopName}</strong>
                                            .
                                            <br /> Kun du som har bestilt kan hente varen
                                            {numberOfItems > 1 ? "e" : ""} (fullmakt godtas ikke).
                                        </Text>
                                    </StepByStep.Description>
                                </StepByStep.Step>
                            </>
                        )}

                        {hasPortingSubscriptions && (
                            <StepByStep.Step>
                                <StepByStep.Description
                                    heading="Aktivering"
                                    icon={<Icon name="wireless" aria-hidden="true" focusable="false" />}
                                >
                                    {Object.keys(portingDates).length > 0 ? (
                                        <PortingDetailsCard />
                                    ) : (
                                        <Text className="receipt-next-steps__step-description">
                                            {TEXT.RECEIPT.ACTIVATION_INFORMATION}
                                        </Text>
                                    )}
                                </StepByStep.Description>
                            </StepByStep.Step>
                        )}
                        {hasTransferSubscriptionFromOtherOwner && (
                            <StepByStep.Step>
                                <StepByStep.Description
                                    heading="Varsel på e-post"
                                    icon={<Icon name="email" aria-hidden="true" focusable="false" />}
                                >
                                    {TEXT.RECEIPT.TRANSFER_INFORMATION}
                                </StepByStep.Description>
                            </StepByStep.Step>
                        )}
                        {hasPortingSubscriptions && (
                            <StepByStep.Step>
                                <StepByStep.Description
                                    heading="SIM-kort"
                                    icon={<Icon name="smartphone" aria-hidden="true" focusable="false" />}
                                >
                                    {TEXT.RECEIPT.SIM_INFORMATION}
                                </StepByStep.Description>
                            </StepByStep.Step>
                        )}
                    </StepByStep>
                </>
            )}
            {hasDoubleDataDisclaimer && (
                <Notification className="receipt-next-steps__info-card" variant="beige">
                    <Notification.Heading as="h3">
                        <Notification.Icon name="info">Greit å vite</Notification.Icon>
                    </Notification.Heading>
                    <Notification.Text>{TEXT.RECEIPT.DOBBEL_DATA_DISCLAIMER}</Notification.Text>
                </Notification>
            )}
        </div>
    );
};

export default ReceiptNextSteps;
